import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package, Database, Timer, Milestone, Server, Router as RouterIcon,
  Cloud, Globe2, Zap, Radio, Signal, Satellite, Cpu, Shield,
  ArrowRight, Check, X, Loader2, Play, RotateCcw, Sparkles, Eye,
  Compass, Hand, Activity, UserCheck, Wrench, BookOpen, Trophy,
  ChevronRight,
} from "lucide-react";
import { Immersive, immersiveSession } from "../../lib/immersive";

const STAGE_ICON = {
  understand: BookOpen,
  explore: Compass,
  interact: Hand,
  simulate: Activity,
  experience: UserCheck,
  solve: Wrench,
  learn: Trophy,
};

const CONCEPT_ICON = { Package, Database, Timer, Milestone, Server, Router: RouterIcon, Cloud, Globe2, Zap };
const NODE_ICON = {
  endpoint: Cpu,
  router: RouterIcon,
  backbone: Satellite,
  cdn: Cloud,
};

/**
 * Reusable ExperienceEngine — the SPINE of the Immersive Learning Lab.
 * Experiences differ only in the `stages[]` content. The engine renders every
 * stage shape (concepts, nodes, protocols, route, scenarios, solve, takeaways)
 * uniformly in Mythos cinematic chrome.
 */
export default function ExperienceEngine({ meta, stages, assessment, accent, accentDark, onFinish, autoAdvanceMs = 0 }) {
  const [idx, setIdx] = useState(0);
  const [showAssessment, setShowAssessment] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const session = immersiveSession();

  const stage = stages[idx];
  const total = stages.length;
  const progress = Math.round(((idx + (showAssessment ? 1 : 0)) / (total + 1)) * 100);

  useEffect(() => {
    Immersive.event("stage_entered", { experience: meta.slug, stage: stage?.key, session_id: session });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [idx, stage?.key, meta.slug, session]);

  useEffect(() => {
    if (!autoAdvanceMs) return undefined;
    const t = setTimeout(() => {
      if (idx < total - 1) setIdx(idx + 1);
    }, autoAdvanceMs);
    return () => clearTimeout(t);
  }, [idx, total, autoAdvanceMs]);

  const next = () => {
    Immersive.event("stage_completed", { experience: meta.slug, stage: stage.key, session_id: session });
    if (idx < total - 1) setIdx(idx + 1);
    else setShowAssessment(true);
  };

  const back = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  const StageIcon = STAGE_ICON[stage?.key] || Sparkles;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#07090D 0%, #0E0F12 100%)" }} data-testid={`exp-engine-${meta.slug}`}>
      {/* Top rail */}
      <div className="sticky top-0 z-20 border-b border-[#2A3349]/60" style={{ background: "rgba(7,9,13,0.72)", backdropFilter: "blur(14px)" }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-3 flex items-center gap-4">
          <Link to="/immersive" className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold hover:opacity-80">
            ← Immersive Lab
          </Link>
          <div className="h-5 w-px bg-[#2A3349]" />
          <div className="font-mythos font-extrabold text-[#F4EFE6] text-sm tracking-wide truncate">{meta.title}</div>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden md:flex items-center gap-1.5">
              {stages.map((s, i) => {
                const Ico = STAGE_ICON[s.key] || Sparkles;
                const active = i === idx && !showAssessment;
                const done = i < idx || showAssessment;
                return (
                  <div key={s.key} className="flex items-center gap-1.5">
                    <div
                      className={`w-7 h-7 rounded-full grid place-items-center border transition-all ${
                        active ? "scale-110 shadow-lg" : ""
                      }`}
                      style={{
                        background: done || active ? accent : "transparent",
                        borderColor: done || active ? accent : "#2A3349",
                        color: done || active ? "#0E0F12" : "#7d8294",
                      }}
                      title={s.label}
                      data-testid={`exp-stage-dot-${s.key}`}
                    >
                      <Ico size={13} strokeWidth={2.3} />
                    </div>
                    {i < stages.length - 1 && <div className="w-3 h-px" style={{ background: done ? accent : "#2A3349" }} />}
                  </div>
                );
              })}
              <div className="w-3 h-px" style={{ background: showAssessment || submitted ? accent : "#2A3349" }} />
              <div className={`w-7 h-7 rounded-full grid place-items-center border`} style={{ background: showAssessment ? accent : "transparent", borderColor: showAssessment ? accent : "#2A3349", color: showAssessment ? "#0E0F12" : "#7d8294" }}>
                <Trophy size={13} strokeWidth={2.3} />
              </div>
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: accent }}>
              {progress}%
            </div>
          </div>
        </div>
        <div className="h-[2px] w-full bg-[#2A3349]/40">
          <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: accent }} />
        </div>
      </div>

      {/* Stage body */}
      <AnimatePresence mode="wait">
        {!showAssessment && stage && (
          <motion.div
            key={stage.key + idx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45 }}
            className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-16"
            data-testid={`exp-stage-${stage.key}`}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: accentDark, border: `1px solid ${accent}` }}>
                <StageIcon size={18} style={{ color: accent }} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: accent }}>
                  Stage 0{idx + 1} · {stage.label}
                </div>
              </div>
            </div>
            <h2 className="font-mythos text-3xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6] max-w-4xl leading-[1.05]">
              {stage.title}
            </h2>
            {stage.subtitle && (
              <p className="mt-4 text-[#E8EBF0]/75 max-w-3xl leading-relaxed md:text-lg">{stage.subtitle}</p>
            )}

            <div className="mt-10">
              {stage.key === "understand" && <UnderstandStage stage={stage} accent={accent} accentDark={accentDark} session={session} slug={meta.slug} />}
              {stage.key === "explore" && <ExploreStage stage={stage} accent={accent} accentDark={accentDark} session={session} slug={meta.slug} />}
              {stage.key === "interact" && <InteractStage stage={stage} accent={accent} accentDark={accentDark} session={session} slug={meta.slug} />}
              {stage.key === "simulate" && <SimulateStage stage={stage} accent={accent} accentDark={accentDark} session={session} slug={meta.slug} />}
              {stage.key === "experience" && <ExperienceStage stage={stage} accent={accent} accentDark={accentDark} session={session} slug={meta.slug} />}
              {stage.key === "solve" && <SolveStage stage={stage} accent={accent} accentDark={accentDark} session={session} slug={meta.slug} />}
              {stage.key === "learn" && <LearnStage stage={stage} accent={accent} accentDark={accentDark} />}
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-3">
              {idx > 0 && (
                <button onClick={back} className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F4EFE6]/60 hover:text-[#F4EFE6] transition" data-testid="exp-back">
                  ← Previous
                </button>
              )}
              <button
                onClick={next}
                className="ml-auto rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition text-[#0E0F12]"
                style={{ background: accent }}
                data-testid="exp-next"
              >
                {idx === total - 1 ? "KNOWLEDGE CHECK" : "CONTINUE"} <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        )}

        {showAssessment && (
          <AssessmentStage
            key="assessment"
            assessment={assessment}
            meta={meta}
            accent={accent}
            accentDark={accentDark}
            session={session}
            onFinish={(score) => { setSubmitted(score); if (onFinish) onFinish(score); }}
            submitted={submitted}
            setSubmitted={setSubmitted}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================
// UNDERSTAND
// =============================================================
function UnderstandStage({ stage, accent, accentDark, session, slug }) {
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stage.concepts.map((c, i) => {
          const Ico = CONCEPT_ICON[c.icon] || Sparkles;
          return (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.09 }}
              className="rounded-2xl p-6 border transition hover:scale-[1.02] cursor-default"
              style={{ background: accentDark, borderColor: `${accent}44` }}
              onClick={() => Immersive.event("concept_explored", { experience: slug, data: { concept: c.name }, session_id: session })}
              data-testid={`exp-concept-${c.name.toLowerCase()}`}
            >
              <div className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: `${accent}22`, border: `1px solid ${accent}66` }}>
                <Ico size={20} style={{ color: accent }} />
              </div>
              <div className="font-mythos font-extrabold text-xl text-[#F4EFE6] tracking-wide">{c.name}</div>
              <div className="text-sm text-[#E8EBF0]/75 mt-2 leading-relaxed">{c.d}</div>
            </motion.div>
          );
        })}
      </div>
      {stage.fact && (
        <div className="mt-8 rounded-2xl p-6 border" style={{ background: "#0A0D15", borderColor: `${accent}55` }}>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2" style={{ color: accent }}>Did you know?</div>
          <p className="text-[#F4EFE6]/90 leading-relaxed">{stage.fact}</p>
        </div>
      )}
    </div>
  );
}

// =============================================================
// EXPLORE — network topology map
// =============================================================
function ExploreStage({ stage, accent, accentDark, session, slug }) {
  const [sel, setSel] = useState(null);
  const nodes = stage.nodes || [];
  const edges = stage.edges || [];
  const nodeById = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);

  const pick = (id) => {
    setSel(id);
    Immersive.event("node_inspected", { experience: slug, stage: "explore", data: { node: id }, session_id: session });
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div
        className="relative rounded-2xl border overflow-hidden"
        style={{ aspectRatio: "16/9", background: "radial-gradient(circle at 30% 20%, #0b2232 0%, #050810 70%)", borderColor: `${accent}44` }}
        data-testid="exp-explore-canvas"
      >
        {/* grid */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <defs>
            <pattern id="g" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke={`${accent}11`} strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#g)" />
        </svg>
        {/* edges */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {edges.map(([a, b], i) => {
            const na = nodeById[a];
            const nb = nodeById[b];
            if (!na || !nb) return null;
            const highlighted = sel === a || sel === b;
            return (
              <line
                key={i}
                x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
                stroke={highlighted ? accent : `${accent}55`}
                strokeWidth={highlighted ? "0.5" : "0.25"}
                strokeDasharray={highlighted ? "0" : "1 1.5"}
                vectorEffect="non-scaling-stroke"
                style={{ transition: "all 0.4s" }}
              />
            );
          })}
          {/* animated pulse */}
          {edges.map(([a, b], i) => {
            const na = nodeById[a]; const nb = nodeById[b];
            if (!na || !nb) return null;
            return (
              <circle key={`p-${i}`} r="0.6" fill={accent} opacity="0.9">
                <animate attributeName="cx" from={na.x} to={nb.x} dur={`${2.5 + (i % 3)}s`} repeatCount="indefinite" />
                <animate attributeName="cy" from={na.y} to={nb.y} dur={`${2.5 + (i % 3)}s`} repeatCount="indefinite" />
                <animate attributeName="opacity" values="0;1;0" dur={`${2.5 + (i % 3)}s`} repeatCount="indefinite" />
              </circle>
            );
          })}
        </svg>
        {/* nodes */}
        {nodes.map((n) => {
          const Ico = NODE_ICON[n.kind] || Server;
          const active = sel === n.id;
          return (
            <button
              key={n.id}
              onClick={() => pick(n.id)}
              className="absolute group"
              style={{ left: `${n.x}%`, top: `${n.y}%`, transform: "translate(-50%,-50%)" }}
              data-testid={`exp-node-${n.id}`}
            >
              <div
                className={`relative w-12 h-12 rounded-full grid place-items-center transition-all ${active ? "scale-125" : "group-hover:scale-110"}`}
                style={{
                  background: active ? accent : accentDark,
                  border: `2px solid ${accent}`,
                  boxShadow: active ? `0 0 24px ${accent}80` : `0 0 8px ${accent}30`,
                }}
              >
                <Ico size={20} style={{ color: active ? "#0E0F12" : accent }} />
                {active && (
                  <span className="absolute -inset-2 rounded-full animate-ping" style={{ border: `1px solid ${accent}` }} />
                )}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 whitespace-nowrap text-[9px] tracking-[0.14em] uppercase font-bold" style={{ color: active ? accent : "#F4EFE6AA" }}>
                {n.label}
              </div>
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl p-6 border" style={{ background: accentDark, borderColor: `${accent}44` }}>
        {sel ? (
          <>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2" style={{ color: accent }}>Node inspected</div>
            <div className="font-mythos font-extrabold text-xl text-[#F4EFE6]">{nodeById[sel]?.label}</div>
            <p className="text-sm text-[#E8EBF0]/80 mt-3 leading-relaxed">{nodeById[sel]?.role}</p>
          </>
        ) : (
          <>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2" style={{ color: accent }}>Instructions</div>
            <p className="text-sm text-[#E8EBF0]/80 leading-relaxed">Tap any node on the map to inspect it. The dotted lines are live links — the dots travelling along them are packets, right now.</p>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================
// INTERACT — protocol picker
// =============================================================
function InteractStage({ stage, accent, accentDark, session, slug }) {
  const [picked, setPicked] = useState(null);
  const pick = (opt) => {
    setPicked(opt);
    Immersive.event("protocol_picked", { experience: slug, stage: "interact", data: { pick: opt.id, correct: opt.feedback_correct }, session_id: session });
  };
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {stage.protocols.map((p, i) => (
        <motion.div
          key={p.name}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.08 }}
          className="rounded-2xl p-6 border"
          style={{ background: accentDark, borderColor: `${p.colour}66` }}
          data-testid={`exp-proto-${p.name}`}
        >
          <div className="font-mythos font-extrabold text-3xl tracking-tight" style={{ color: p.colour }}>{p.name}</div>
          <p className="text-sm text-[#E8EBF0]/85 mt-3 leading-relaxed">{p.d}</p>
          <div className="text-xs text-[#E8EBF0]/55 mt-3">Trade-off: {p.trade}</div>
        </motion.div>
      ))}
      <div className="lg:col-span-3 rounded-2xl p-6 border mt-2" style={{ background: "#0A0D15", borderColor: `${accent}55` }}>
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3" style={{ color: accent }}>Decision</div>
        <div className="font-mythos text-xl text-[#F4EFE6] mb-5">{stage.picker_label}</div>
        <div className="flex flex-wrap gap-3">
          {stage.picker_options.map((o) => {
            const chosen = picked?.id === o.id;
            return (
              <button
                key={o.id}
                onClick={() => !picked && pick(o)}
                disabled={!!picked}
                className="rounded-full px-5 py-2.5 font-bold tracking-wide transition"
                style={{
                  background: chosen ? accent : "transparent",
                  color: chosen ? "#0E0F12" : "#F4EFE6",
                  border: `1px solid ${chosen ? accent : "#2A3349"}`,
                  opacity: picked && !chosen ? 0.4 : 1,
                }}
                data-testid={`exp-proto-pick-${o.id}`}
              >
                {o.id}
              </button>
            );
          })}
        </div>
        {picked && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 pt-5 border-t border-[#2A3349]">
            <div className="flex items-start gap-3">
              {picked.feedback_correct ? (
                <Check size={18} style={{ color: accent }} className="mt-0.5 flex-shrink-0" />
              ) : (
                <X size={18} className="text-[#FF7A6B] mt-0.5 flex-shrink-0" />
              )}
              <div className="text-sm text-[#F4EFE6]/90 leading-relaxed">{picked.feedback}</div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// =============================================================
// SIMULATE — animated packet journey
// =============================================================
function SimulateStage({ stage, accent, accentDark }) {
  const [step, setStep] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const route = stage.route || [];
  const narration = stage.narration || [];

  useEffect(() => {
    if (!playing) return undefined;
    if (step >= route.length - 1) { setPlaying(false); return undefined; }
    const t = setTimeout(() => setStep(step + 1), 1600);
    return () => clearTimeout(t);
  }, [playing, step, route.length]);

  const play = () => { setStep(0); setPlaying(true); };
  const replay = () => { setStep(-1); setTimeout(play, 150); };

  return (
    <div>
      <div className="rounded-2xl border overflow-hidden" style={{ background: "radial-gradient(circle at 30% 20%, #0b2232 0%, #050810 70%)", borderColor: `${accent}44` }}>
        <div className="relative" style={{ aspectRatio: "16/5" }}>
          <div className="absolute inset-0 flex items-center justify-between px-10">
            {route.map((id, i) => {
              const reached = step >= i;
              const current = step === i;
              return (
                <div key={id} className="relative flex flex-col items-center" data-testid={`exp-sim-node-${id}`}>
                  <div
                    className="w-10 h-10 md:w-14 md:h-14 rounded-full grid place-items-center transition-all"
                    style={{
                      background: reached ? accent : accentDark,
                      border: `2px solid ${accent}`,
                      transform: current ? "scale(1.3)" : "scale(1)",
                      boxShadow: current ? `0 0 32px ${accent}` : "none",
                    }}
                  >
                    <Server size={18} style={{ color: reached ? "#0E0F12" : accent }} />
                  </div>
                  <div className="text-[9px] tracking-[0.14em] uppercase font-bold mt-2 text-[#F4EFE6]/70">{id}</div>
                </div>
              );
            })}
          </div>
          {/* path line */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
            <line x1="7" y1="50" x2="93" y2="50" stroke={`${accent}44`} strokeWidth="0.3" vectorEffect="non-scaling-stroke" strokeDasharray="1 1.2" />
            {step >= 0 && (
              <line x1="7" y1="50" x2={7 + ((93 - 7) * (step + 1)) / route.length} y2="50" stroke={accent} strokeWidth="0.5" vectorEffect="non-scaling-stroke" style={{ transition: "all 1.4s ease-out" }} />
            )}
          </svg>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-[1fr_auto] gap-4 items-center">
        <div className="rounded-xl p-5 border min-h-[90px]" style={{ background: accentDark, borderColor: `${accent}44` }}>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2" style={{ color: accent }}>Narration</div>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="text-[#F4EFE6] text-sm md:text-base"
            >
              {step < 0 ? "Press play to launch a packet from your device." : narration[step]}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex gap-3">
          <button onClick={step < 0 ? play : replay} className="rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 text-[#0E0F12]" style={{ background: accent }} data-testid="exp-sim-play">
            {step < 0 ? (<><Play size={14} /> PLAY</>) : (<><RotateCcw size={14} /> REPLAY</>)}
          </button>
        </div>
      </div>

      {stage.fact && (
        <div className="mt-6 rounded-2xl p-5 border" style={{ background: "#0A0D15", borderColor: `${accent}55` }}>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2" style={{ color: accent }}>Reality check</div>
          <p className="text-[#F4EFE6]/85 text-sm leading-relaxed">{stage.fact}</p>
        </div>
      )}
    </div>
  );
}

// =============================================================
// EXPERIENCE — you are the packet
// =============================================================
function ExperienceStage({ stage, accent, accentDark, session, slug }) {
  const [path, setPath] = useState([]);
  const [stats, setStats] = useState({ latency: 0, loss: 0, optimal: 0 });
  const scenarios = stage.scenarios || [];
  const idx = path.length;
  const scenario = scenarios[idx];
  const done = idx >= scenarios.length;

  const choose = (opt) => {
    const best = scenarios[idx].best === opt.to;
    const newPath = [...path, { from: scenario.from, to: opt.to, latency: opt.latency_ms, loss: opt.loss_pct, best, note: opt.note, insight: scenario.insight }];
    setPath(newPath);
    setStats((s) => ({
      latency: s.latency + opt.latency_ms,
      loss: Math.max(s.loss, opt.loss_pct),
      optimal: s.optimal + (best ? 1 : 0),
    }));
    Immersive.event("route_chosen", { experience: slug, stage: "experience", data: { from: scenario.from, to: opt.to, best }, session_id: session });
  };

  const reset = () => { setPath([]); setStats({ latency: 0, loss: 0, optimal: 0 }); };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="rounded-2xl p-6 border" style={{ background: accentDark, borderColor: `${accent}44` }}>
        {!done && scenario && (
          <>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3" style={{ color: accent }}>
              HOP {idx + 1} of {scenarios.length} · FROM {scenario.from.toUpperCase()}
            </div>
            <div className="font-mythos text-xl text-[#F4EFE6] mb-5">Pick your next node.</div>
            <div className="space-y-3">
              {scenario.options.map((o) => (
                <button
                  key={o.to}
                  onClick={() => choose(o)}
                  className="w-full text-left rounded-xl p-5 border transition hover:scale-[1.01]"
                  style={{ background: "#0A0D15", borderColor: `${accent}33` }}
                  data-testid={`exp-route-${o.to}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-mythos font-bold text-lg" style={{ color: accent }}>→ {o.to.toUpperCase()}</div>
                      <div className="text-xs text-[#E8EBF0]/70 mt-1">{o.note}</div>
                    </div>
                    <div className="flex gap-2 text-[10px] tracking-[0.14em] uppercase font-bold">
                      <span className="rounded-full px-2 py-1 border" style={{ borderColor: `${accent}66`, color: accent }}>{o.latency_ms} ms</span>
                      <span className="rounded-full px-2 py-1 border" style={{ borderColor: "#2A3349", color: "#F4EFE6AA" }}>Loss {o.loss_pct}%</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {path.length > 0 && (
              <div className="mt-6 pt-6 border-t border-[#2A3349]">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: accent }}>Last hop insight</div>
                <div className="text-sm text-[#E8EBF0]/85 leading-relaxed">{path[path.length - 1].insight}</div>
              </div>
            )}
          </>
        )}
        {done && (
          <>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3" style={{ color: accent }}>Journey complete</div>
            <div className="font-mythos text-2xl text-[#F4EFE6]">You delivered the packet.</div>
            <div className="grid grid-cols-3 gap-3 mt-6">
              <Stat label="Latency" value={`${stats.latency} ms`} accent={accent} />
              <Stat label="Peak loss" value={`${stats.loss}%`} accent={accent} />
              <Stat label="Optimal hops" value={`${stats.optimal}/${scenarios.length}`} accent={accent} />
            </div>
            <button onClick={reset} className="mt-6 text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: accent }} data-testid="exp-route-reset">
              <RotateCcw size={12} className="inline mr-1" /> Replay the journey
            </button>
          </>
        )}
      </div>
      <div className="rounded-2xl p-5 border" style={{ background: "#0A0D15", borderColor: `${accent}55` }}>
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3" style={{ color: accent }}>Live stats</div>
        <Stat label="Total latency" value={`${stats.latency} ms`} accent={accent} />
        <div className="h-3" />
        <Stat label="Worst packet loss" value={`${stats.loss}%`} accent={accent} />
        <div className="h-3" />
        <Stat label="Optimal hops chosen" value={`${stats.optimal} / ${scenarios.length}`} accent={accent} />
        <div className="mt-5 pt-5 border-t border-[#2A3349] text-xs text-[#E8EBF0]/60 leading-relaxed">
          Every microsecond, real BGP routers answer this exact question across millions of packets.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#F4EFE6]/55">{label}</div>
      <div className="font-mythos font-extrabold text-2xl mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

// =============================================================
// SOLVE — fix the broken network
// =============================================================
function SolveStage({ stage, accent, accentDark, session, slug }) {
  const [picked, setPicked] = useState(null);
  const choose = (c) => {
    setPicked(c);
    Immersive.event("solve_submitted", { experience: slug, stage: "solve", data: { letter: c.letter, right: c.right }, session_id: session });
  };
  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-6">
      <div className="rounded-2xl p-6 border" style={{ background: "#0A0D15", borderColor: `${accent}55` }}>
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3" style={{ color: accent }}>Live symptom</div>
        <p className="text-sm text-[#F4EFE6]/90 leading-relaxed">{stage.symptom}</p>
        <div className="mt-6 flex items-center gap-2 text-xs">
          <Signal size={14} style={{ color: accent }} />
          <div className="text-[#F4EFE6]/60">Users are complaining in real time.</div>
        </div>
      </div>
      <div className="space-y-3">
        {stage.choices.map((c, i) => {
          const chosen = picked?.letter === c.letter;
          return (
            <motion.button
              key={c.letter}
              initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
              onClick={() => !picked && choose(c)}
              disabled={!!picked}
              className="w-full text-left rounded-2xl p-6 border transition"
              style={{
                background: accentDark,
                borderColor: chosen ? accent : `${accent}33`,
                opacity: picked && !chosen ? 0.5 : 1,
              }}
              data-testid={`exp-solve-${c.letter}`}
            >
              <div className="flex items-start gap-4">
                <div className="font-mythos font-extrabold text-3xl" style={{ color: accent }}>{c.letter}</div>
                <div className="flex-1">
                  <div className="font-mythos font-bold text-lg text-[#F4EFE6]">{c.title}</div>
                  {chosen && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-3 pt-3 border-t border-[#2A3349]">
                      <div className="flex items-start gap-2">
                        {c.right ? <Check size={16} style={{ color: accent }} className="mt-0.5" /> : <X size={16} className="text-[#FF7A6B] mt-0.5" />}
                        <div className="text-sm text-[#F4EFE6]/85 leading-relaxed">{c.feedback}</div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================
// LEARN
// =============================================================
function LearnStage({ stage, accent, accentDark }) {
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-4">
        {stage.takeaways.map((t, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            className="rounded-xl p-5 border flex gap-4 items-start"
            style={{ background: accentDark, borderColor: `${accent}33` }}
          >
            <div className="font-mythos font-extrabold text-2xl leading-none mt-1" style={{ color: accent }}>0{i + 1}</div>
            <p className="text-sm text-[#F4EFE6]/85 leading-relaxed">{t}</p>
          </motion.div>
        ))}
      </div>
      {stage.real_world && (
        <div className="mt-10">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-4" style={{ color: accent }}>In your pocket, right now</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {stage.real_world.map((r, i) => (
              <div key={i} className="rounded-xl p-5 border" style={{ background: "#0A0D15", borderColor: `${accent}33` }}>
                <div className="font-mythos font-extrabold" style={{ color: accent }}>{r.name}</div>
                <div className="text-xs text-[#E8EBF0]/70 mt-2 leading-relaxed">{r.d}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================
// ASSESSMENT
// =============================================================
function AssessmentStage({ assessment, meta, accent, accentDark, session, onFinish, submitted, setSubmitted }) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { Immersive.event("assessment_started", { experience: meta.slug, session_id: session }); }, [meta.slug, session]);

  const q = assessment[index];
  const chosen = answers[index];
  const pick = (i) => setAnswers([...answers.slice(0, index), i, ...answers.slice(index + 1)]);

  const next = async () => {
    if (index < assessment.length - 1) {
      setIndex(index + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setLoading(true);
      try {
        const final = answers.slice(0, assessment.length);
        while (final.length < assessment.length) final.push(-1);
        const r = await Immersive.submitAssessment(meta.slug, final, session);
        setSubmitted(r);
        Immersive.event("assessment_completed", { experience: meta.slug, data: { score: r.score }, session_id: session });
        if (onFinish) onFinish(r);
      } finally {
        setLoading(false);
      }
    }
  };

  if (submitted) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-5 md:px-10 py-16" data-testid="exp-assessment-result">
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3" style={{ color: accent }}>Final · Knowledge discovery</div>
        <h2 className="font-mythos text-4xl md:text-5xl font-extrabold tracking-tight text-[#F4EFE6]">You understand the internet now.</h2>
        <div className="mt-8 rounded-2xl p-8 border text-center" style={{ background: accentDark, borderColor: accent }}>
          <div className="font-mythos font-extrabold text-7xl" style={{ color: accent }}>{submitted.score}<span className="text-3xl text-[#F4EFE6]/40"> / {submitted.total}</span></div>
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold mt-2" style={{ color: accent }}>Concepts mastered</div>
        </div>
        <div className="mt-8 space-y-3">
          {submitted.results.map((r, i) => (
            <div key={i} className="rounded-xl p-4 border" style={{ background: accentDark, borderColor: r.correct ? `${accent}66` : "#2A3349" }} data-testid={`exp-result-${i}`}>
              <div className="flex items-start gap-3">
                {r.correct ? <Check size={16} style={{ color: accent }} className="mt-0.5" /> : <X size={16} className="text-[#7d8294] mt-0.5" />}
                <div className="text-xs text-[#E8EBF0]/85 leading-relaxed">{r.explanation}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/immersive" className="rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 text-[#0E0F12]" style={{ background: accent }} data-testid="exp-done-back">
            Try another experience <ArrowRight size={16} />
          </Link>
          <Link to="/immersive/future" className="rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 border transition" style={{ color: accent, borderColor: accent }}>
            SHOW ME THE FUTURE <Sparkles size={16} />
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-5 md:px-10 py-16" data-testid="exp-assessment">
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3" style={{ color: accent }}>Knowledge check · {index + 1} / {assessment.length}</div>
      <h3 className="font-mythos text-2xl md:text-3xl font-extrabold text-[#F4EFE6] leading-tight">{q.prompt}</h3>
      <div className="mt-8 space-y-3">
        {q.options.map((o, i) => (
          <button
            key={i}
            onClick={() => pick(i)}
            data-testid={`exp-ass-q${index}-opt${i}`}
            className="w-full text-left rounded-xl p-4 border transition hover:scale-[1.005]"
            style={{ background: accentDark, borderColor: chosen === i ? accent : "#2A3349" }}
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold" style={{ background: chosen === i ? accent : "transparent", color: chosen === i ? "#0E0F12" : "#F4EFE6", border: chosen === i ? "none" : "1px solid #2A3349" }}>
                {String.fromCharCode(65 + i)}
              </div>
              <div className="text-sm text-[#F4EFE6]/90">{o}</div>
            </div>
          </button>
        ))}
      </div>
      <button onClick={next} disabled={chosen === undefined || loading} className="mt-10 rounded-full px-6 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-50 text-[#0E0F12]" style={{ background: accent }} data-testid="exp-ass-next">
        {loading ? <Loader2 className="animate-spin" size={16} /> : index === assessment.length - 1 ? "SEE RESULT" : "NEXT"} <ArrowRight size={16} />
      </button>
    </motion.div>
  );
}
